import type {
  ActionContext,
  ActionTree,
  GetterTree,
  Module,
  MutationTree,
  Store,
} from "vuex";
import type { State } from "@/store/state";
import type {
  ModelParameter,
  ModelParameterUpdate,
} from "@/interfaces/modelparameter";
import { eventMonitor } from "@/api/eventMonitor";

export interface ModelParameterState {
  modelParameters: ModelParameter[];
}

const defaultState: ModelParameterState = {
  modelParameters: [],
};

// Getters
const getters: GetterTree<ModelParameterState, State> = {
  modelParameters: (state): ModelParameter[] => {
    return state.modelParameters;
  },
};

const NS = "modelParameters/";

export const readModelParameters = (store: Store<State>) =>
  store.getters[`${NS}modelParameters`] as ModelParameter[];

// Mutations

const mutations: MutationTree<ModelParameterState> = {
  setModelParameters(state, modelParameters: ModelParameter[]): void {
    state.modelParameters = modelParameters;
  },
  setModelParameter(state, modelparameter: ModelParameter): void {
    const parameters = state.modelParameters.filter(
      (a: ModelParameter) => a.id !== modelparameter.id,
    );
    parameters.push(modelparameter);
    state.modelParameters = parameters;
  },
};

export const commitSetModelParameters = (
  store: Store<State>,
  payload: ModelParameter[],
): void => {
  store.commit(`${NS}setModelParameters`, payload);
};

export const commitSetModelParameter = (
  store: Store<State>,
  payload: ModelParameter,
): void => {
  store.commit(`${NS}setModelParameter`, payload);
};

// Actions

type ModelParameterContext = ActionContext<ModelParameterState, State>;

const actions: ActionTree<ModelParameterState, State> = {
  async actionGetModelParameters(
    context: ModelParameterContext,
  ): Promise<void> {
    try {
      const response = await eventMonitor.getModelParameters(
        context.rootState.main.token,
      );
      context.commit("setModelParameters", response.data);
    } catch (error) {
      await context.dispatch("main/actionCheckApiError", error, { root: true });
    }
  },

  async actionUpdateModelParameter(
    context: ModelParameterContext,
    payload: ModelParameterUpdate,
  ): Promise<void> {
    try {
      const response = await eventMonitor.updateModelParameters(
        context.rootState.main.token,
        payload,
      );
      context.commit("setModelParameter", response.data);
    } catch (error) {
      await context.dispatch("main/actionCheckApiError", error, { root: true });
    }
  },
};

export const dispatchActionGetModelParameters = (store: Store<State>) =>
  store.dispatch(`${NS}actionGetModelParameters`);

export const dispatchActionUpdateModelParameter = (
  store: Store<State>,
  payload: ModelParameterUpdate,
) => store.dispatch(`${NS}actionUpdateModelParameter`, payload);

// Module

export const modelParametersModule: Module<ModelParameterState, State> = {
  namespaced: true,
  state: defaultState,
  mutations,
  actions,
  getters,
};
